import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Modal, Tooltip } from '@cimpress/react-components';
import { SkuSelector, PriceModelRevisionSelector } from '@cimpress-technology/ff-react-components';
import { notify } from 'reapop';
import { auth } from '../../../utils/auth';
import { css } from 'react-emotion';
import omit from 'lodash/omit';
import { withSnackbar } from '../../snackbar/Snackbar';
import memoizeOne from 'memoize-one';
import Button from '../../common/Button';
import { actions as termActions, selectors as termSelectors } from '../../../reducers/terms';
import { connect } from 'react-redux';
import { isProduction } from '../../../utilities/processFunctions';
import { FFS_EMAIL_ADDRESS } from '../../../constants/appConstants';
import { TermType } from '../../../constants/termTypes';
import { bindActionCreators } from 'redux';
import { withTranslation } from 'react-i18next';

const buttonStyle = css`
  margin-right: 10px;
`;

const hidden = css`
  display: none;
`;

export class EditSkusModal extends PureComponent {
  static propTypes = {
    editModalOpened: PropTypes.bool.isRequired,
    termRevision: PropTypes.object.isRequired,
    skusMap: PropTypes.object.isRequired,

    /* The price model to represent the current term. This is currently the price model of the first item in the current term revision.
     * This is fine for list price terms with multiple price models, because this flow will grab the list price model for skus upon creation.
     * This is problematic when a special term has multiple price models, but this flow should not be enabled in such a case.
     * */
    priceModel: PropTypes.object.isRequired,
    setLatestRevision: PropTypes.func.isRequired,
    updateTermItems: PropTypes.func.isRequired,
    resetUpdateTermItemsStatus: PropTypes.func.isRequired,
    onCloseEditSkusModal: PropTypes.func,
    isItemsUpdating: PropTypes.bool,
    isItemsUpdated: PropTypes.bool,
    hasItemsUpdateFailed: PropTypes.bool
  };

  constructor(props) {
    super(props);

    this.state = {
      selectedSkusMap: {},
      latestCompatiblePriceModels: [],
      compatiblePriceModels: [],
      isChangingPricing: false,
      selectedPriceModel: props.priceModel,
      isCompatiblePriceModelLoading: false,
      isCompatiblePriceModelLoaded: false
    };
  }

  componentDidMount() {
    this.setState({ selectedSkusMap: this.props.skusMap });
  }

  componentWillUnmount() {
    this.props.resetUpdateTermItemsStatus();
  }

  onAddSku = skuInfo => {
    this.setState({
      selectedSkusMap: {
        ...this.state.selectedSkusMap,
        [skuInfo.sku]: skuInfo
      },
      selectedPriceModel: this.props.priceModel
    });
  };

  onRemoveSku = skuInfo => {
    this.setState({
      selectedSkusMap: omit(this.state.selectedSkusMap, skuInfo.sku),
      selectedPriceModel: this.props.priceModel
    });
  };

  onBulkAddSkus = skus => {
    const skuList = Object.keys(skus);
    const duplicateSkus = skuList.filter(sku => this.state.selectedSkusMap[sku]);
    const numberOfskusToAdd = skuList.length;
    const numberOfSkusAdded = skuList.length - duplicateSkus.length;
    const { notify, t } = this.props;

    notify({
      title: t('contractActions:editSkusModal.successful'),
      message: `${t('contractActions:editSkusModal.bulkAddSkusMessage', { numberOfSkusAdded })} ${
        numberOfskusToAdd !== numberOfSkusAdded
          ? t('contractActions:editSkusModal.bulkAddDuplicateMessage', { duplicateSkus: duplicateSkus.length })
          : ''
      }`,
      status: 'info',
      dismissible: true,
      dismissAfter: 5000
    });

    this.setState({
      selectedSkusMap: { ...this.state.selectedSkusMap, ...skus },
      selectedPriceModel: this.props.priceModel
    });
  };

  onBulkRemoveSkus = skus => {
    this.setState({
      selectedSkusMap: omit(this.state.selectedSkusMap, skus),
      selectedPriceModel: this.props.priceModel
    });
  };

  getTermSkus = memoizeOne(termSkusMap => (termSkusMap ? Object.keys(termSkusMap) : []));
  getSelectedSkus = memoizeOne(selectedSkusMap => (selectedSkusMap ? Object.keys(selectedSkusMap) : []));

  onSaveAndPublish = () => {
    const newPriceModel =
      this.props.termRevision.type === TermType.SPECIAL_PRICE ||
      this.props.termRevision.type === TermType.SHIPPING_PRICE
        ? this.state.selectedPriceModel || this.props.priceModel
        : null;

    this.props.updateTermItems(Object.keys(this.state.selectedSkusMap), newPriceModel).then(() => {
      this.props.resetUpdateTermItemsStatus();
      this.props.setLatestRevision({
        termId: this.props.termRevision.termId,
        revisionId: this.props.termRevision.revisionId + 1
      });
    });
  };

  onLoadingCompatiblePriceModels = () =>
    this.setState({ isCompatiblePriceModelLoading: true, isCompatiblePriceModelLoaded: false });

  onCompatiblePriceModelsLoaded = (latestPMs, allPMs) => {
    this.setState({
      latestCompatiblePriceModels: latestPMs,
      compatiblePriceModels: allPMs,
      isCompatiblePriceModelLoading: false,
      isCompatiblePriceModelLoaded: true
    });
  };

  toggleIsChangingPricing = () => {
    // if turning off this option, reset the state to what it was before
    if (this.state.isChangingPricing) {
      this.setState({ selectedPriceModel: this.props.priceModel });
    }
    this.setState({ isChangingPricing: !this.state.isChangingPricing });
  };

  onSelectPriceModel = (id, revisionId, name) => {
    let priceModel = { id, revisionId, name };

    if (!priceModel.id || !priceModel.revisionId) {
      priceModel = this.props.priceModel;
    }

    this.setState({ selectedPriceModel: priceModel });
  };

  render() {
    const { selectedSkusMap, selectedPriceModel, compatiblePriceModels, isCompatiblePriceModelLoading } = this.state;
    const {
      skusMap,
      editModalOpened,
      onCloseEditSkusModal,
      termRevision,
      isItemsUpdating,
      isItemsUpdated,
      priceModel,
      t
    } = this.props;

    const selectedSkus = this.getSelectedSkus(selectedSkusMap);
    const termSkus = this.getTermSkus(skusMap);

    const addedSkus = selectedSkus.filter(s => !skusMap[s]);
    const removedSkus = termSkus.filter(s => !selectedSkusMap[s]);

    const showChangePricingOption = termRevision.type === TermType.SPECIAL_PRICE;

    const hasChangesToPricing =
      selectedPriceModel.id &&
      selectedPriceModel.revisionId &&
      (selectedPriceModel.id !== priceModel.id || selectedPriceModel.revisionId !== priceModel.revisionId);

    const hasChanges = addedSkus.length + removedSkus.length > 0 || (showChangePricingOption && hasChangesToPricing);

    const isSelectedPriceModelCompatible =
      !isCompatiblePriceModelLoading &&
      compatiblePriceModels.some(
        ({ id, revisionId }) => selectedPriceModel.id === id && selectedPriceModel.revisionId === revisionId
      );

    const skuSelector = (
      <SkuSelector
        addNonExistentSkus
        locale="en"
        skuSearchSectionTitle={t('createContract:addProducts.searchAndAddProducts')}
        skuBinSectionTitle={t('createContract:addProducts.selectedProducts')}
        sellerFulfillerId={
          termRevision.seller && termRevision.seller.type === 'fulfillers' ? termRevision.seller.id : null
        }
        selectedSkus={selectedSkusMap}
        onAddSku={this.onAddSku}
        onRemoveSku={this.onRemoveSku}
        onBulkAddSkus={this.onBulkAddSkus}
        onBulkRemoveSkus={this.onBulkRemoveSkus}
        // we need this props in order to allow bulk removals
        unremoveableSkuList={[]}
        onGetAccessToken={auth.getAccessToken}
        marketable={false}
        skipLoadDescriptionsForBulkAddSkus={true}
      />
    );

    const bodyToRender = skuSelector;

    const tooltipMsg = !hasChanges ? (
      <div>{t('contractActions:editSkusModal.toolTipNoChangesMessage')}</div>
    ) : selectedSkus.length === 0 ? (
      <div>{t('contractActions:editSkusModal.toolTipOneOrMoreChangesMessage')}</div>
    ) : showChangePricingOption && !isSelectedPriceModelCompatible ? (
      <div>{t('contractActions:editSkusModal.toolTipNotCompatibleMessage')}</div>
    ) : (
      <div>
        <div>{t('contractActions:editSkusModal.newRevisionActionMessage')}</div>
        <br />
        <div>
          {!addedSkus.length ? (
            t('contractActions:editSkusModal.noProductsAddedMessage')
          ) : (
            <b>{t('contractActions:editSkusModal.productsAddedMessage', { products: addedSkus.length })}</b>
          )}
        </div>
        <div>
          {!removedSkus.length ? (
            t('contractActions:editSkusModal.noProductsRemovedMessage')
          ) : (
            <b>{t('contractActions:editSkusModal.productsRemovedMessage', { products: removedSkus.length })}</b>
          )}
        </div>
        {showChangePricingOption && (
          <div>
            {!hasChangesToPricing ? (
              t('contractActions:editSkusModal.noChangesToPricingMessage')
            ) : (
              <b>
                {t('contractActions:editSkusModal.priceModelChangedMessage', {
                  priceModel: selectedPriceModel.name || selectedPriceModel.id
                })}
              </b>
            )}
          </div>
        )}
      </div>
    );

    const errMsg = t('createContract:pricingContract.noPriceModelFoundMessage', {
      contactEmailAddress: FFS_EMAIL_ADDRESS
    });

    return (
      <Modal
        // need to adjust the z-index property because the modal from SkuSelector will not be visible so set it right behind (1050 - 1)
        style={{ minWidth: '60vw', zIndex: '1049' }}
        wrapperStyle={{ zIndex: '1049' }}
        className="edit-skus-modal"
        bsSize="lg"
        show={editModalOpened}
        onRequestHide={onCloseEditSkusModal}
        title={t('contractActions:editSkusModal.title')}
        closeButton={true}
        footer={
          <div>
            <button className={`btn btn -default ${buttonStyle} `} onClick={onCloseEditSkusModal}>
              {t('common:commonWords.close')}
            </button>
            <Tooltip contents={tooltipMsg} variety="popover">
              <Button
                className={`btn btn-primary ${buttonStyle} `}
                onClick={this.onSaveAndPublish}
                disabled={
                  isItemsUpdating ||
                  isItemsUpdated ||
                  selectedSkus.length === 0 ||
                  !hasChanges ||
                  (showChangePricingOption && !isSelectedPriceModelCompatible)
                }>
                {isItemsUpdating
                  ? t('contractActions:editSkusModal.updating')
                  : isItemsUpdated
                    ? t('contractActions:editSkusModal.updated')
                    : t('contractActions:saveAndUpdate')}
              </Button>
            </Tooltip>
          </div>
        }>
        {bodyToRender}
        <div />
        {showChangePricingOption && (
          <Checkbox
            label={t('contractActions:editSkusModal.checkboxLabel')}
            checked={this.state.isChangingPricing}
            payload="disable"
            onChange={this.toggleIsChangingPricing}
          />
        )}
        {termRevision.type === TermType.SPECIAL_PRICE && (
          <PriceModelRevisionSelector
            className={this.state.isChangingPricing ? null : hidden}
            isProduction={isProduction}
            priceModelId={selectedPriceModel.id}
            revisionId={selectedPriceModel.revisionId}
            sellerId={termRevision.seller.id}
            sellerType={termRevision.seller.type}
            skus={selectedSkus}
            onGetAccessToken={auth.getAccessToken}
            onLoadingRevisions={this.onLoadingCompatiblePriceModels}
            onGetAllRevisions={this.onCompatiblePriceModelsLoaded}
            onChange={this.onSelectPriceModel}
            errorMessage={errMsg}
            filterArchivedPriceModels={true}
            filterListPriceModels={true}
          />
        )}
      </Modal>
    );
  }
}

const mapStateToProps = state => ({
  isItemsUpdating: termSelectors.getIsTermItemsUpdating(state),
  isItemsUpdated: termSelectors.getIsTermItemsUpdated(state),
  hasItemsUpdateFailed: termSelectors.getHasTermItemsUpdateFailed(state)
});

const mapDispatchToProps = (dispatch, ownProps) => {
  const { termRevision } = ownProps;
  return {
    updateTermItems: (skus, priceModel) =>
      dispatch(termActions.updateTermItems(termRevision.termId, termRevision.revisionId, skus, priceModel)),
    resetUpdateTermItemsStatus: () => dispatch(termActions.resetUpdateTermItemsStatus()),
    notify: bindActionCreators(notify, dispatch)
  };
};

export default withSnackbar(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withTranslation()(EditSkusModal))
);
